'use client'
import { useComponentTranslation } from '@core/i18n'
import {
  Button,
  Flex,
  FocusLock,
  HStack,
  Icon,
  Input,
  Text,
  useNumberInput,
} from '@core/ui-elements'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { MdRemove } from 'react-icons/md'

export interface IAmountInput {
  disabled?: boolean
  value: number
  setValue: Dispatch<SetStateAction<number>>
  maxValue?: number
  minValue?: number
  selectAll?: boolean
  show2plus?: boolean
  size?: 'sm' | 'md' | 'lg'
  alwaysDisplayRemoveButton?: boolean
  autoFocus?: boolean
  enableInput?: boolean
  setIsInvalidValue?: (value: boolean) => void
  onSubmit?: () => void
}

export const AmountInput: FC<IAmountInput> = ({
  value,
  setValue,
  maxValue = 100000,
  minValue = 0,
  selectAll = true,
  show2plus = false,
  size = 'lg',
  alwaysDisplayRemoveButton = false,
  autoFocus = false,
  enableInput = false,
  setIsInvalidValue,
  onSubmit,
}) => {
  const t = useComponentTranslation('AmountInput')

  const [invalidValue, setInvalidValue] = useState<string | null>(null)
  const [valueAsString, setValueAsString] = useState<string>(value.toString())

  const fontSize = {
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
  }

  const buttonProps = {
    variant: 'ghost',
    colorScheme: 'gray',
    backgroundColor: 'transparent',
    fontWeight: 'normal',
    fontSize: fontSize[size],
    size: size,
  }

  const handleSelectAll = () => {
    setIsInvalidValue?.(false)
    setValue(maxValue)
  }

  const handleIncrement = useCallback(
    (amount: number) => {
      setValue((prevState) => prevState + amount)
    },
    [setValue]
  )

  const handleDecrement = useCallback(
    (amount: number) => {
      setValue((prevState) => prevState - amount)
    },
    [setValue]
  )

  useEffect(
    function syncValueAsStringWithValue() {
      setValueAsString(value.toString())
    },
    [value]
  )

  const { getInputProps } = useNumberInput({
    step: 1,
    defaultValue: value,
    min: minValue,
    max: maxValue,
    precision: 0,
    isReadOnly: !enableInput,
    isDisabled: !enableInput,
    inputMode: 'numeric',
    pattern: '[0-9]',
    value: valueAsString,
    onChange(value, valueAsNumber) {
      setInvalidValue(null)
      setValueAsString(value)
      if (!value || Number.isNaN(valueAsNumber)) {
        setIsInvalidValue?.(true)
      }
      if (valueAsNumber) {
        setValue(Math.trunc(valueAsNumber))
        setIsInvalidValue?.(false)
      } else {
        setIsInvalidValue?.(true)
      }
    },
    onInvalid(message, value) {
      setInvalidValue(value)
      setIsInvalidValue?.(true)
    },
  })

  const input = getInputProps()

  return (
    <Flex flexDirection={'column'} gap={'0.5rem'} justifyContent={'center'}>
      <HStack alignContent={'center'} gap={'0.5rem'} justifyContent={'center'}>
        {value > minValue || alwaysDisplayRemoveButton ? (
          <Button
            {...buttonProps}
            aria-label="minus"
            isDisabled={value <= minValue}
            onClick={() => handleDecrement(1)}
          >
            <Icon
              as={MdRemove}
              data-testid={MdRemove.name}
              fontSize={fontSize[size]}
            />
          </Button>
        ) : null}
        <FocusLock autoFocus={autoFocus}>
          <Input
            _disabled={{
              opacity: '1',
              cursor: 'default',
            }}
            _hover={{
              backgroundColor: value ? 'blue.100' : 'gray.100',
            }}
            _invalid={{
              backgroundColor: 'red.100',
              borderColor: 'red.500',
            }}
            data-testid="value-amount-input"
            htmlSize={value.toString().length}
            padding="0.25rem"
            size={size}
            textAlign="center"
            type="number"
            variant="outline"
            width="3rem"
            {...input}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !invalidValue) {
                onSubmit?.()
              }
            }}
          />
        </FocusLock>

        <Button
          {...buttonProps}
          aria-label="plus"
          isDisabled={value >= maxValue}
          onClick={() => {
            handleIncrement(1)

            if (value >= minValue) {
              setIsInvalidValue?.(false)
            }
          }}
        >
          +1
        </Button>
        {show2plus ? (
          <Button
            {...buttonProps}
            aria-label="plus2"
            isDisabled={value > maxValue - 2}
            onClick={() => handleIncrement(2)}
          >
            +2
          </Button>
        ) : null}
      </HStack>

      {invalidValue && (
        <Text color="red.500" fontSize="0.75rem" textAlign="center">
          {t('Cannot collect')} &quot;{invalidValue}&quot;
        </Text>
      )}

      {selectAll && value < maxValue ? (
        <Button size={'md'} onClick={handleSelectAll}>
          {t('Select all')}
        </Button>
      ) : null}
    </Flex>
  )
}
